import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getProfile } from '../../services/profileService';
import { getCurrentUser, updateUserMetadata } from '../../services/authService';
import ProfileForm from '../../components/Profile/ProfileForm';
import { debugProfileSubmission } from '../../debug-profile';

/**
 * EditProfile component for editing user profile information
 * This component is used both for regular profile editing and for the required profile completion step
 */
const EditProfile = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if this is a required profile completion
  const requireCompletion = location.state?.requireCompletion || false;
  const message = location.state?.message || null;
  const missingFields = location.state?.missingFields || [];

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        
        // Get the current user
        const { user, error: userError } = await getCurrentUser();
        
        if (userError) throw userError;
        
        if (user) {
          setUser(user);
          
          // Fetch the user's profile
          const { profile, error: profileError } = await getProfile(user.id);
            
          if (profileError) {
            throw profileError;
          }
          
          // Set the profile (might be null if it doesn't exist yet)
          setProfile(profile);
        } else {
          // Redirect to login if no user is found
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching profile:', error.message);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [navigate]);

  const handleProfileUpdate = async (updatedProfile) => {
    try {
      console.log('Profile update handler called with:', updatedProfile);
      
      // Debug the profile submission
      const debugResult = debugProfileSubmission({
        firstName: updatedProfile.first_name,
        lastName: updatedProfile.last_name,
        userType: updatedProfile.user_type,
        venueName: updatedProfile.venue_name,
        venueType: updatedProfile.venue_type,
        professions: updatedProfile.professions
      });
      
      console.log('Debug result:', debugResult);
      
      // If this was a required profile completion, update the user metadata
      if (requireCompletion && user) {
        console.log('Updating user metadata to mark profile as completed');
        // Update user metadata to indicate profile has been completed
        await updateUserMetadata(user.id, {
          profile_setup_required: false
        });
        
        console.log('User metadata updated to indicate profile completion');
      }
      
      setProfile(updatedProfile);
      
      // Always redirect to dashboard after profile creation/update
      console.log('Profile updated successfully, redirecting to dashboard');
      
      // Use a timeout to ensure the state is updated before navigation
      setTimeout(() => {
        navigate('/', { 
          state: { 
            message: requireCompletion ? 'Profile completed successfully! Welcome to Unissn!' : 'Profile updated successfully!' 
          } 
        });
      }, 100);
    } catch (error) {
      console.error('Error updating user metadata:', error);
      setError('Profile was updated but there was an error updating user settings.');
    }
  };

  const handleCancel = () => {
    // If this is a required completion, don't allow cancellation
    if (requireCompletion) {
      setError('Please complete your profile to continue using the app.');
      return;
    }
    
    // Otherwise, go back to profile view
    navigate('/profile');
  };

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error!</h4>
          <p>{error}</p>
          {requireCompletion && (
            <button 
              className="btn btn-primary mt-2" 
              onClick={() => setError(null)}
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h1>{requireCompletion ? 'Complete Your Profile' : 'Edit Profile'}</h1>
      
      {message && (
        <div className="alert alert-info mb-4" role="alert">
          {message}
        </div>
      )}
      
      {requireCompletion && (
        <div className="alert alert-warning mb-4" role="alert">
          <strong>Profile Completion Required</strong>
          <p>Please complete your profile information to continue using the app. This helps us provide you with a better experience.</p>
          {Array.isArray(missingFields) && missingFields.length > 0 && (
            <div className="mt-2">
              <div>Missing:</div>
              <ul className="mb-0">
                {missingFields.map((f, idx) => (
                  <li key={idx}>{f}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      <ProfileForm 
        initialData={profile} 
        userId={user.id} 
        onSuccess={handleProfileUpdate} 
        onCancel={handleCancel} 
        isRequired={requireCompletion}
      />
    </div>
  );
};

export default EditProfile;
